import { Injectable } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { Store } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { from } from "rxjs";
import {
  catchError,
  exhaustMap,
  map,
  tap,
  switchMap,
  mergeMap,
} from "rxjs/operators";

import * as DeliveriesActionTypes from "../../actions/deliveries/deliveries.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";
import { AppState } from "../../reducers";

import { DeliveriesService } from "src/app/shared/services/delivery/delivery.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";

@Injectable()
export class DeliveriesEffects {
  getDeliveries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveriesActionTypes.getDeliveries),
      switchMap((action) =>
        this.deliveryService
          .getDeliveries(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              DeliveriesActionTypes.loadDeliveries({ response: result })
            ),
            catchError((error) => {
              const actions = [
                DeliveriesActionTypes.loadDeliveriesLoadEnd(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "DeliveryManagement.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  getMeasurements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveriesActionTypes.getMeasurements),
      switchMap((action) =>
        this.deliveryService
          .getDeliveries(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              DeliveriesActionTypes.loadMeasurements({ response: result })
            ),
            catchError((error) => {
              const actions = [
                DeliveriesActionTypes.loadDeliveriesLoadEnd(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "DeliveryManagement.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getDebits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveriesActionTypes.getDebits),
      switchMap((action) =>
        this.deliveryService
          .getDeliveries(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              DeliveriesActionTypes.loadDebits({ response: result })
            ),
            catchError((error) => {
              const actions = [
                DeliveriesActionTypes.loadDeliveriesLoadEnd(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "DeliveryManagement.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  createDelivery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveriesActionTypes.createDelivery),
      exhaustMap((action) =>
        this.deliveryService.createDelivery(action).pipe(
          mergeMap((result) => [
            DeliveriesActionTypes.resetCreation(),
            DeliveriesActionTypes.getDelivery({
              delivery: result?.result?.code,
            }),
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  action.delivery?.movementTypeCode === "SET"
                    ? "DeliveryManagement.MeasurementCreated"
                    : "DeliveryManagement.DeliveryCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              DeliveriesActionTypes.loadDeliveriesLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "DeliveryManagement.DeliveryCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateDelivery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveriesActionTypes.updateDelivery),
      exhaustMap((action) =>
        this.deliveryService.updateDelivery(action.delivery).pipe(
          map((result) => {
            return DeliveriesActionTypes.getDelivery({
              delivery: result?.result?.code,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  action.delivery?.movementTypeCode === "SET"
                   ? "DeliveryManagement.measurementUpdated"
                   : "DeliveryManagement.deliveryUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              DeliveriesActionTypes.loadDeliveriesLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "DeliveryManagement.DeliveryCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getProductByWarehouse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveriesActionTypes.getProduct),
      exhaustMap((action) =>
        this.deliveryService.getProductByWarehouse(action.warehouseCode, action.pageReq).pipe(
          map((result) =>
            DeliveriesActionTypes.loadProduct({ response: result })
          ),
          catchError((error) => {
            const actions = [
              DeliveriesActionTypes.loadProductLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "DeliveryMagagement.getProductByWarehouseError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getDelivery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveriesActionTypes.getDelivery),
      exhaustMap((action) =>
        this.deliveryService.getDelivery(action.delivery).pipe(
          map((result) =>
            DeliveriesActionTypes.loadDelivery({ response: result })
          ),
          catchError((error) => {
            const actions = [
              DeliveriesActionTypes.loadDeliveryLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "DeliveryManagement.failedGetDelivery",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getDeliveriesExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveriesActionTypes.getDeliveriesExcel),
      exhaustMap((action) =>
        this.deliveryService
          .getDeliveries(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              DeliveriesActionTypes.loadDeliveriesExcel({ response: result })
            ),
            catchError((error) => {
              const actions = [
                DeliveriesActionTypes.loadDeliveriesExcel({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "DeliveryManagement.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getMeasurementsExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveriesActionTypes.getMeasurementsExcel),
      exhaustMap((action) =>
        this.deliveryService
          .getDeliveries(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              DeliveriesActionTypes.loadMeasurementsExcel({ response: result })
            ),
            catchError((error) => {
              const actions = [
                DeliveriesActionTypes.loadMeasurementsExcel({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "DeliveryManagement.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  getDebitsExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveriesActionTypes.getDebitExcel),
      exhaustMap((action) =>
        this.deliveryService
          .getDeliveries(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              DeliveriesActionTypes.loadDebitExcel({ response: result })
            ),
            catchError((error) => {
              const actions = [
                DeliveriesActionTypes.loadDebitExcel({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "DeliveryManagement.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Deliveries Table Config
  getDeliveriesTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveriesActionTypes.getDeliveriesTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              DeliveriesActionTypes.loadDeliveriesTableConfig({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                DeliveriesActionTypes.loadDeliveriesTableConfig({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Measurements Table Config
  getMeasurementsTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveriesActionTypes.getMeasurementsTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              DeliveriesActionTypes.loadMeasurementsTableConfig({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                DeliveriesActionTypes.loadMeasurementsTableConfig({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

   // Get Debits Table Config
   getDebitsTableConfig$ = createEffect(() =>
   this.actions$.pipe(
     ofType(DeliveriesActionTypes.getDebitsTableConfig),
     exhaustMap((action) =>
       this.tableConfigService
         .getConfigSettings(action.options.code, action.options.settingType)
         .pipe(
           map((result) =>
             DeliveriesActionTypes.loadDebitsTableConfig({
               response: result,
             })
           ),
           catchError((error) => {
             const actions = [
               DeliveriesActionTypes.loadDebitsTableConfig({
                 response: [],
               }),
               MessagesActionTypes.setSnackbar(
                 this.buildSnackbarObj(
                   "TableConfig.LoadError",
                   SnackbarType.ERROR
                 )
               ),
             ];
             return from(actions);
           })
         )
     )
   )
 );

  // Save Table Config
  saveTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveriesActionTypes.saveTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            DeliveriesActionTypes.loadDeliveriesLoadEnd(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "TableConfig.SaveSuccess",
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              DeliveriesActionTypes.loadDeliveriesLoadEnd(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private deliveryService: DeliveriesService,
    private translate: TranslateService,
    private tableConfigService: TableConfigService
  ) {}

  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }
}
